/** ---------------------------------------------------------------------------------------
 * --  LICENSE AGREEMENT -- *
 This software is licensed under the following terms:

 * -- GRANT OF LICENSE | RESTRICTIONS -- *
 Subject to the terms and conditions of this license, the copyright holders
 hereby grant you a non-exclusive, non-transferable, revocable license to use,
 copy, modify, and distribute the software.
 You are expressly prohibited from selling or commercially exploiting the software.
 Unauthorized use, reproduction, or distribution of the software, or any part
 thereof, is strictly prohibited.

 * -- AUTHORIZED USERS | INTELLECTUAL PROPERTY -- *
 This license is granted only to the individual or entity to whom the software is
 directly distributed by the copyright holders. Any use by other individuals or
 entities is unauthorized and in violation of this license. The software is
 protected by copyright and other intellectual property laws.
 The copyright holders retain all rights, title, and interest in the software.

 * -- GOVERNING LAW -- *
 This license is governed by and construed in accordance with the laws of Romania.
 Any legal action or proceeding related to this license shall be brought exclusively
 in the courts of Romania.

 [Weddify x Diaphan Stationery] © [2024] All rights reserved.
 -------------------------------------------------------------------------------------- **/

import React, {useEffect, useRef, useState} from 'react';
import {FaChevronDown} from "react-icons/fa6";
import AppStatic from "@/utils/AppStatic.tsx";

type FaqItemType = {
    title: string;
    content: string;
};

interface AppFaqProps {
    faqData: FaqItemType[];
    faqTitleColor?: string;
    faqContentColor?: string;
    faqBackgroundColor?: string;
}

const AppFaq: React.FC<AppFaqProps> = ({faqData, faqTitleColor, faqContentColor, faqBackgroundColor}) => {
    const [openIndexes, setOpenIndexes] = useState<number[]>([]);
    const toggle = (index: number) => {
        setOpenIndexes(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    return (
        <div className={`pb-10 bg-white/10 ${faqBackgroundColor}`}>
            <h3 className={`text-4xl font-bold md:text-6xl mb-6 ${faqTitleColor}`}>
                {AppStatic.appFaqTitle.ro.translation}
            </h3>
            <div className="w-full mx-auto">
                <div className="space-y-2">
                    {faqData.map((item, index) => {
                        const isOpen = openIndexes.includes(index);
                        return <FaqItem faqContentColor={faqContentColor} key={index} item={item} index={index}
                                        isOpen={isOpen} toggle={toggle}/>;
                    })}
                </div>
            </div>
        </div>
    );
};

const FaqItem: React.FC<{
    item: FaqItemType;
    index: number;
    isOpen: boolean;
    faqContentColor?: string;
    toggle: (index: number) => void;
}> = ({item, index, isOpen, toggle, faqContentColor}) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(0);

    useEffect(() => {
        if (isOpen && contentRef.current) {
            setHeight(contentRef.current.scrollHeight);
        } else {
            setHeight(0);
        }
    }, [isOpen]);

    return (
        <div className={`overflow-hidden border-b border-gray-400 ${faqContentColor}`}>
            <button
                onClick={() => toggle(index)}
                className="w-full flex justify-between items-center py-3 text-left transition cursor-pointer">
                <span className="tracking-wider font-thin text-base">{item.title}</span>
                <FaChevronDown
                    className={`transition-transform duration-300 ${
                        isOpen ? "rotate-180" : ""
                    }`}
                />
            </button>
            <div ref={contentRef}
                 style={{maxHeight: `${height}px`}}
                 className="transition-all duration-300 ease-in-out overflow-hidden px-1">
                <div className={`py-2 tracking-wider font-thin text-black ${faqContentColor}`}>{item.content}</div>
            </div>
        </div>
    );
};

export default AppFaq;